import type { IdentityProvider } from "Api/Api";
import type {
  AccessRightWithPermissionsDto,
  PartyType,
  Region,
  UserStatus,
  Version,
} from "Infrastructure/Api/Api";

export type AppUser = {
  login: string;
  firstName: string;
  lastName: string;
  accessRoleCode: string | null;
  partyType: PartyType | null;
  accessRights: Array<AccessRightWithPermissionsDto>;
  isFirstSignIn: boolean;
  tokenExpiration: string;
  userIdentityProviders: IdentityProvider[];
  profilePicture: string | null;
  /**
   * PublicID of the active role context (the sign-in one by default) — the own avatar loads
   * through the context photo route keyed by it. Null only for non-migrated users, which fall
   * back to the legacy person photo route.
   */
  activeContextPublicID: string | null;
  userStatus: UserStatus;
  version?: Version | null;
  partyPublicID?: string;
  userPublicID: string;
  userID: number;
  /**
   * True when this non-Customer User has not yet been linked to an Identity.
   * Frontend routes these users to the migration flow before granting app access.
   */
  migrationRequired: boolean;
  /**
   * True when the active (migrated) role has no region yet — required for every role. Customers pick it
   * in the region modal; business roles (Merchant/Hunter/HunterPlus) pick it inline in the onboarding form.
   */
  regionRequired: boolean;
  /**
   * Region of the active context (null only before a region is set). For a Hunter it identifies the
   * country their own company is registered in — used purely to pick the business register (ARES/RPO)
   * for company lookup; currency and settlement are driven by the license, not this region.
   */
  region?: Region | null;
  /**
   * The active persona's handle (@nick) — strictly per-persona, null until the user picks one.
   */
  handle?: string | null;
  /**
   * True when the active (migrated) persona has no handle yet — drives the dashboard
   * HandleMissingBanner nudge. The handle deliberately is NOT a ProfileCompletionModal
   * requirement: its onboarding step is skippable and hard enforcement lives at the
   * chat/social feature gates. Always false for non-migrated users (no persona to hang a
   * handle on) and for non-social roles (Employee/Admin/back-office are never prompted).
   */
  handleRequired?: boolean;
};

export enum SignInStep {
  Credentials = 0,
  VerificationToken = 1,
}

export enum ResetPasswordStep {
  Email = 0,
  Success = 1,
}

export enum SetPasswordStep {
  Password = 0,
  Success = 1,
}
